// ======================= Imports =======================
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./css/InvoicesMain.css";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import moment from "moment";
import { api } from "../../../../../../../../../utility/AxiosInceptor";
import CommonLoaderBlue from "../../../../../../../../components/Loader/LoaderBlue";
import NotFoundData from "../../Common/Notfound/Notfound";
import { InvoiceActionIcon } from "./assets/Assets";
import { OverlayPanel } from "primereact/overlaypanel";
import { VoidInvoice } from "./dialogs/VoidInvoice/VoidInvoice";
import { WaiveLateFee } from "./dialogs/WaiveLateFee/WaiveLateFee";
import { AdjustAmount } from "./dialogs/AdjustAmount/AdjustAmount";
import { DownloadInvoice } from "./dialogs/DownloadInvoice/DownoadInvoice";
import { PaidExternally } from "./dialogs/PaidExternally/PaidExternally";
import { MailInvoice } from "./dialogs/EmailInvoice/EmailInvoice";
import { ChangeInvoiceDueDate } from "./dialogs/ChangeDueDate/ChangeDueDate";

// ======================= Main Component =======================
export const InvoicesMain = ({ refreshAllpayment, cpData }) => {
  // ======================= State Management =======================
  const [allInvoicesData, setAllInvoicesData] = useState([]);
  const [isInvoiceLoading, setIsInvoiceLoading] = useState(true);
  const [isAPiCalled, setIsApiCalled] = useState(false);
  const [currentInvoiceSelected, setCurrentInvoiceSelected] = useState({});
  const [selectedAction, setSelectedAction] = useState("");
  const [overlayContent, setOverlayContent] = useState("");
  const [currentActiveDialog, setCurrentActiveDialog] = useState({
    void: false,
    lateFee: false,
    adjustInvoice: false,
    download: false,
    paidExternally: false,
    sendEmail: false,
    changeDueDate: false,
  });

  // ======================= Refs =======================
  let invoiceActionRef = useRef(null);
  const statusOverlayRef = useRef(null);

  // ======================= API: Fetch Invoices =======================
  const getInvoice = useCallback(async () => {
    try {
      const invoiceResponse = await api.get(
        `/api/web/invoices/getinvoicebycustomerid?customerid=${cpData._id}`
      );
      setAllInvoicesData(invoiceResponse?.data?.data);
      setIsApiCalled(true);
      setIsInvoiceLoading(false);
    } catch (err) {
      setIsInvoiceLoading(false);
    }
  }, [cpData._id]); // Only recreate when cpData._id changes

  // ======================= Effects =======================
  useEffect(() => {
    if (cpData !== undefined && selectedAction === "") {
      getInvoice();
    }
  }, [selectedAction, refreshAllpayment, cpData, getInvoice]);

  // ======================= Render =======================
  return (
    <div className="main-invoice-container">
      {/* ======================= Dialogs ======================= */}
      {currentActiveDialog.void ? (
        <VoidInvoice
          setSelectedAction={setSelectedAction}
          cpData={cpData}
          currentInvoice={currentInvoiceSelected}
          setCurrentActiveDialog={setCurrentActiveDialog}
        />
      ) : currentActiveDialog?.lateFee ? (
        <WaiveLateFee
          setSelectedAction={setSelectedAction}
          cpData={cpData}
          currentInvoice={currentInvoiceSelected}
          setCurrentActiveDialog={setCurrentActiveDialog}
        />
      ) : currentActiveDialog?.adjustInvoice ? (
        <AdjustAmount
          setSelectedAction={setSelectedAction}
          cpData={cpData}
          currentInvoice={currentInvoiceSelected}
          setCurrentActiveDialog={setCurrentActiveDialog}
        />
      ) : currentActiveDialog?.download ? (
        <DownloadInvoice
          setSelectedAction={setSelectedAction}
          cpData={cpData}
          currentInvoice={currentInvoiceSelected}
          setCurrentActiveDialog={setCurrentActiveDialog}
        />
      ) : currentActiveDialog?.paidExternally ? (
        <PaidExternally
          setSelectedAction={setSelectedAction}
          cpData={cpData}
          currentInvoice={currentInvoiceSelected}
          setCurrentActiveDialog={setCurrentActiveDialog}
        />
      ) : currentActiveDialog?.sendEmail ? (
        <MailInvoice
          setSelectedAction={setSelectedAction}
          cpData={cpData}
          currentInvoice={currentInvoiceSelected}
          setCurrentActiveDialog={setCurrentActiveDialog}
        />
      ) : currentActiveDialog?.changeDueDate ? (
        <ChangeInvoiceDueDate
          setSelectedAction={setSelectedAction}
          cpData={cpData}
          currentInvoice={currentInvoiceSelected}
          setCurrentActiveDialog={setCurrentActiveDialog}
        />
      ) : undefined}

      {/* ======================= No Data Found ======================= */}
      {!isInvoiceLoading && isAPiCalled && allInvoicesData.length === 0 ? (
        <NotFoundData currentNoFoundData={"invoice"} />
      ) : (
        // ======================= Data Table =======================
        <DataTable value={allInvoicesData}>
          {/* ======================= Invoice No ======================= */}
          <Column
            field="invoiceNo"
            header="Invoice No"
            body={(rowData) => (
              <>
                {rowData?.invoiceVoid && (
                  <div
                    style={{
                      position: "absolute",
                      marginTop: "0.9rem",
                      width: "100%",
                      borderTop: "solid #9c9eb6 1px",
                    }}
                  ></div>
                )}{" "}
                <span style={{ cursor: "pointer" }}>
                  {rowData?.invoiceNo}
                </span>{" "}
              </>
            )}
          />
          {/* ======================= Invoice Status ======================= */}
          <Column
            field="invoiceStatus"
            body={(rowData) => (
              <p
                className={`${rowData.invoiceStatus === "Unpaid" ? "invoice-status-unpaid" : rowData?.invoiceStatus === "Partial" ? "invoice-status-partial" : "invoice-status-paid"}`}
              >
                {rowData.invoiceStatus}
              </p>
            )}
            header="Invoice Status"
          />
          {/* ======================= Created Date ======================= */}
          <Column
            header="Created Date"
            body={(rowData) => {
              return (
                <span>
                  {moment
                    .tz(rowData.createdAt, "America/New_York")
                    .format("MM-DD-YYYY")}
                </span>
              );
            }}
          />
          {/* ======================= Invoice Type ======================= */}
          <Column
            field="invoiceType"
            header="Type"
            body={(rowData) => (
              <>
                <p
                  onMouseEnter={(e) => {
                    if (rowData?.invoiceType.length > 15) {
                      setOverlayContent(rowData?.invoiceType);
                      statusOverlayRef.current.show(e);
                    }
                  }}
                  onMouseLeave={() => statusOverlayRef.current.hide()}
                >
                  {rowData?.invoiceType.length > 15
                    ? rowData?.invoiceType?.substring(0, 12) + "..."
                    : rowData.invoiceType}
                </p>
                <OverlayPanel ref={statusOverlayRef}>
                  <div style={{ padding: "0.5rem 1rem" }}>
                    <p
                      style={{
                        fontWeight: 400,
                        fontSize: "1.4rem",
                        overflow: "hidden",
                        border: "none",
                        margin: "0rem",
                        lineHeight: "1.8rem",
                        color: "#72728f",
                      }}
                    >
                      {" "}
                      {overlayContent}
                    </p>
                  </div>
                </OverlayPanel>
              </>
            )}
          />
          {/* ======================= Amount Paid ======================= */}
          <Column
            field="amountPaid"
            header="Amount Paid"
            body={(rowData) =>
              rowData?.amountPaid !== undefined &&
              rowData?.amountPaid !== "0.00" &&
              rowData?.amountPaid !== "0" &&
              rowData?.amountPaid !== "NaN" &&
              rowData?.amountPaid !== ""
                ? parseFloat(rowData?.amountPaid).toFixed(2)
                : "NIL"
            }
          />
          {/* ======================= Current Bill ======================= */}
          <Column
            field="currentBalance"
            header="Current Bill"
            body={(rowData) =>
              rowData?.currentBalance !== undefined &&
              rowData?.currentBalance !== "NaN" &&
              rowData?.currentBalance !== ""
                ? parseFloat(rowData?.currentBalance).toFixed(2)
                : "NIL"
            }
          />
          {/* ======================= Previous Balance ======================= */}
          <Column
            field="previousBalance"
            body={(rowData) =>
              rowData?.previousBalance !== undefined &&
              rowData?.previousBalance !== "NaN" &&
              rowData?.previousBalance !== ""
                ? parseFloat(rowData?.previousBalance).toFixed(2)
                : "NIL"
            }
            header="Previous Balance"
          />
          {/* ======================= Total Due ======================= */}
          <Column
            field="dueAmount"
            body={(rowData) => {
              return (
                <p>
                  {rowData?.dueAmount !== "NaN" &&
                  rowData?.dueAmount !== "0.00" &&
                  rowData?.dueAmount !== "0" &&
                  rowData?.dueAmount !== null &&
                  rowData?.dueAmount !== "" &&
                  rowData?.dueAmount !== undefined
                    ? rowData?.dueAmount
                    : "NIL"}
                </p>
              );
            }}
            header="Total Due"
          />
          {/* ======================= One Time Charges ======================= */}
          <Column
            field="invoiceOneTimeCharges"
            header="One Time Charges(A)"
            body={(rowData) =>
              parseFloat(rowData.invoiceOneTimeCharges).toFixed(2)
            }
          />
          {/* ======================= Plan Charges ======================= */}
          <Column
            field="planCharges"
            header="Plan Charges(B)"
            body={(rowData) =>
              rowData.planCharges ? rowData.planCharges : "NIL"
            }
          />
          {/* ======================= Additional Charges ======================= */}
          <Column
            field="additionalCharges"
            header="Additional Charges(C)"
            body={(rowData) => {
              const totalAmount = rowData?.additionalCharges.reduce(
                (total, charge) => total + parseFloat(charge.amount),
                0
              );
              return (
                <span>{totalAmount ? totalAmount.toFixed(2) : "NIL"}</span>
              );
            }}
          />
          {/* ======================= Discounts ======================= */}
          <Column
            field="discount"
            header="Discounts"
            body={(rowData) => {
              const totalAmount = rowData?.discount.reduce(
                (total, charge) => total + parseFloat(charge.amount),
                0
              );
              return (
                <span>{totalAmount ? totalAmount.toFixed(2) : "NIL"}</span>
              );
            }}
          />
          {/* ======================= Shipment Cost ======================= */}
          <Column
            field="shipmentCost.amount"
            body={(rowData) => {
              return (
                <p>
                  {rowData?.shipmentCost?.amount !== undefined &&
                  rowData?.shipmentCost?.amount !== null &&
                  rowData?.shipmentCost?.amount !== "NaN" &&
                  rowData?.shipmentCost?.amount !== ""
                    ? rowData?.shipmentCost?.amount
                    : "NIL"}
                </p>
              );
            }}
            header="Shipment Cost"
          />
          {/* ======================= Late Fee ======================= */}
          <Column
            field="lateFee"
            header="Late Fee"
            body={(rowData) =>
              rowData.lateFee && rowData.lateFee !== "0"
                ? rowData.lateFee
                : "NIL"
            }
          />
          {/* ======================= Taxes ======================= */}
          <Column
            field="taxe"
            header="Taxes"
            body={(rowData) => (rowData.taxe ? rowData.taxe : "NIL")}
          />
          {/* ======================= Due Date ======================= */}
          <Column
            field="invoiceDueDate"
            header="Due Date"
            body={(rowData) =>
              rowData.invoiceDueDate && rowData?.invoiceDueDate !== "-"
                ? rowData.invoiceDueDate
                : "NIL"
            }
          />
          {/* ======================= Created By ======================= */}
          <Column
            field="createdBy.name"
            header="Created By"
            body={(rowData) => (
              <>
                <p
                  onMouseEnter={(e) => {
                    if (rowData?.createdBy?.name?.length > 15) {
                      setOverlayContent(rowData?.createdBy?.name);
                      statusOverlayRef.current.show(e);
                    }
                  }}
                  onMouseLeave={() => statusOverlayRef.current.hide()}
                >
                  {rowData?.createdBy?.name?.length > 13
                    ? rowData?.createdBy?.name?.substring(0, 10) + "..."
                    : rowData.createdBy?.name?.length === 0
                      ? "NIL"
                      : rowData?.createdBy?.name}
                </p>
                <OverlayPanel ref={statusOverlayRef}>
                  <div style={{ padding: "0.5rem 1rem" }}>
                    <p
                      style={{
                        fontWeight: 400,
                        fontSize: "1.4rem",
                        overflow: "hidden",
                        border: "none",
                        margin: "0rem",
                        lineHeight: "1.8rem",
                        color: "#72728f",
                      }}
                    >
                      {" "}
                      {overlayContent}
                    </p>
                  </div>
                </OverlayPanel>
              </>
            )}
          />
          {/* ======================= Paid Using ======================= */}
          <Column
            field="invoicePaymentMethod"
            header="Paid Using"
            body={(rowData) => {
              // Remove null, undefined, and empty strings
              const methods = (rowData?.invoicePaymentMethod || []).filter(
                (item) => item && item.trim() !== ""
              );

              const fullText = methods.join(", ");
              const displayText =
                fullText.length === 0
                  ? "NIL"
                  : fullText.length > 15
                    ? fullText.substring(0, 15) + "..."
                    : fullText;

              return (
                <>
                  <p
                    onMouseEnter={(e) => {
                      if (fullText.length > 15) {
                        setOverlayContent(fullText);
                        statusOverlayRef.current.show(e);
                      }
                    }}
                    onMouseLeave={() => statusOverlayRef.current.hide()}
                  >
                    {displayText}
                  </p>
                  <OverlayPanel ref={statusOverlayRef}>
                    <div style={{ padding: "0.5rem 1rem" }}>
                      <p
                        style={{
                          fontWeight: 400,
                          fontSize: "1.4rem",
                          overflow: "hidden",
                          border: "none",
                          margin: "0rem",
                          lineHeight: "1.8rem",
                          color: "#72728f",
                        }}
                      >
                        {overlayContent}
                      </p>
                    </div>
                  </OverlayPanel>
                </>
              );
            }}
          />

          {/* ======================= Confirmation No ======================= */}
          <Column
            field="transId"
            header="Confirmation No"
            body={(rowData) => {
              // Helper to label array items and remove null/undefined/empty strings
              const labelItems = (arr = [], label) =>
                (arr || [])
                  .filter((item) => item && item.toString().trim() !== "")
                  .map((item) => `${label}: ${item}`);

              // Combine all arrays with proper labels
              const combinedArr = [
                ...labelItems(rowData.transId, "Transaction ID"),
                ...labelItems(rowData.paymentIds, "Transaction ID"),
                ...labelItems(rowData.checkNumber, "Cheque Number"),
                ...labelItems(rowData.recieptNumber, "Receipt Number"),
              ];

              const fullText = combinedArr.join(", ");
              const displayText =
                fullText.length === 0
                  ? "NIL"
                  : fullText.length > 15
                    ? fullText.substring(0, 15) + "..."
                    : fullText;

              return (
                <>
                  <p
                    onMouseEnter={(e) => {
                      if (fullText.length > 15) {
                        setOverlayContent(fullText);
                        statusOverlayRef.current.show(e);
                      }
                    }}
                    onMouseLeave={() => statusOverlayRef.current.hide()}
                  >
                    {displayText}
                  </p>
                  <OverlayPanel ref={statusOverlayRef}>
                    <div style={{ padding: "0.5rem 1rem" }}>
                      <p
                        style={{
                          fontWeight: 400,
                          fontSize: "1.4rem",
                          overflow: "hidden",
                          border: "none",
                          margin: "0rem",
                          lineHeight: "1.8rem",
                          color: "#72728f",
                        }}
                      >
                        {overlayContent}
                      </p>
                    </div>
                  </OverlayPanel>
                </>
              );
            }}
          />

          {/* ======================= Actions ======================= */}
          <Column
            field="Actions"
            header="Actions"
            body={(rowData) => {
              return (
                <div className="flex flex-wrap flex-row justify-content-end  w-full h-full align-items-center main-action-wrapper">
                  <div
                    onClick={(e) => {
                      invoiceActionRef.current.toggle(e);
                      setCurrentInvoiceSelected(rowData);
                    }}
                    className="actionbtnmain"
                    style={{ marginTop: "0.4rem" }}
                  >
                    <InvoiceActionIcon />
                  </div>
                  <OverlayPanel ref={invoiceActionRef}>
                    <div className=" main-invoice-overlay-container main-module-container">
                      {/* ======================= Void ======================= */}
                      <p
                        onClick={() => {
                          setSelectedAction("Void");
                          setCurrentActiveDialog({
                            void: true,
                            lateFee: false,
                            adjustInvoice: false,
                            download: false,
                            changeDueDate: false,
                            sendEmail: false,
                          });
                          setTimeout(() => {
                            var element2 = document.getElementById(
                              "void-invoice-container"
                            );
                            element2.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }, 200);
                        }}
                        className={`invoice-item-here ${selectedAction === "Void" ? "selected-module" : "NIL"}`}
                      >
                        Void
                      </p>
                      {/* ======================= Late Fee ======================= */}
                      <p
                        className={` invoice-item-here  ${selectedAction === "latefee" ? "selected-module" : "NIL"}`}
                        onClick={() => {
                          setSelectedAction("latefee");
                          setCurrentActiveDialog({
                            void: false,
                            lateFee: true,
                            adjustInvoice: false,
                            download: false,
                            changeDueDate: false,
                            sendEmail: false,
                          });
                          setTimeout(() => {
                            var element2 = document.getElementById(
                              "void-invoice-container"
                            );
                            element2.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }, 200);
                        }}
                      >
                        Late Fee
                      </p>
                      {/* ======================= Due Date ======================= */}
                      <p
                        className={` invoice-item-here  ${selectedAction === "paidExternally" ? "selected-module" : "NIL"}`}
                        onClick={() => {
                          setSelectedAction("paidexternally");
                          setCurrentActiveDialog({
                            void: false,
                            lateFee: false,
                            adjustInvoice: false,
                            paidExternally: false,
                            download: false,
                            sendEmail: false,
                            changeDueDate: true,
                          });
                          setTimeout(() => {
                            var element2 = document.getElementById(
                              "changeduedate-container"
                            );
                            element2.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }, 200);
                        }}
                      >
                        Due Date
                      </p>
                      {/* ======================= Paid Externally ======================= */}
                      <p
                        className={` invoice-item-here  ${selectedAction === "paidExternally" ? "selected-module" : "NIL"}`}
                        onClick={() => {
                          setSelectedAction("paidexternally");
                          setCurrentActiveDialog({
                            void: false,
                            lateFee: false,
                            adjustInvoice: false,
                            paidExternally: true,
                            download: false,
                            sendEmail: false,
                            changeDueDate: false,
                          });
                          setTimeout(() => {
                            var element2 = document.getElementById(
                              "paid-externally-container"
                            );
                            element2.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }, 200);
                        }}
                      >
                        Paid Externally
                      </p>
                      {/* ======================= Adjust Invoice ======================= */}
                      <p
                        onClick={() => {
                          setSelectedAction("adjustinvoice");
                          setCurrentActiveDialog({
                            void: false,
                            lateFee: false,
                            adjustInvoice: true,
                            download: false,
                            changeDueDate: false,
                            sendEmail: false,
                            paidExternally: false,
                          });
                          setTimeout(() => {
                            var element2 = document.getElementById(
                              "invoice-adjust-main"
                            );
                            element2.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }, 200);
                        }}
                        className={` invoice-item-here  ${selectedAction === "adjustinvoice" ? "selected-module" : "NIL"}`}
                      >
                        Adjust Invoice
                      </p>
                      {/* ======================= Download ======================= */}
                      <p
                        className={` invoice-item-here ${selectedAction === "download" ? "selected-module" : "NIL"}`}
                        onClick={() => {
                          setSelectedAction("download");
                          setCurrentActiveDialog({
                            void: false,
                            lateFee: false,
                            adjustInvoice: false,
                            download: true,
                            sendEmail: false,
                            changeDueDate: false,
                            paidExternally: false,
                          });
                          setTimeout(() => {
                            var element2 = document.getElementById(
                              "invoice-download-main"
                            );
                            element2.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }, 200);
                        }}
                      >
                        Download
                      </p>
                      {/* ======================= Send Email ======================= */}
                      <p
                        className={` invoice-item-here ${selectedAction === "sendemail" ? "selected-module" : "NIL"}`}
                        onClick={() => {
                          setSelectedAction("sendemail");
                          setCurrentActiveDialog({
                            void: false,
                            lateFee: false,
                            adjustInvoice: false,
                            download: false,
                            sendEmail: true,
                            changeDueDate: false,
                            paidExternally: false,
                          });
                          setTimeout(() => {
                            var element2 = document.getElementById(
                              "mail-invoice-container"
                            );
                            element2.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }, 200);
                        }}
                      >
                        Send Email
                      </p>
                    </div>
                  </OverlayPanel>
                </div>
              );
            }}
          />
        </DataTable>
      )}
      {/* ======================= Loader ======================= */}
      {isInvoiceLoading ? (
        <div className="loader-wrapper">
          <CommonLoaderBlue color="royalblue" sizehere={21} />
        </div>
      ) : (
        ""
      )}
    </div>
  );
};
